import React from 'react';
import { BrainCircuit, Crown, Zap, Layers, MapPin, TrendingUp, ChevronRight, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CHART_GRADIENT_CSS } from './chartTheme';
import { useStore } from '../../../lib/store';
import { CURRENCIES } from '../../../../constants';

interface StoreRank {
  storeName: string;
  total: number;
  matches: number;
  coverage: number;
}

interface SplitItem {
  name: string;
  store: string;
  price: number;
  // 'verified' = Lattice/personal history, 'estimate' = Live Market Discovery.
  // Not surfaced visually here — this card groups route items by store, and a
  // single store's route entries can mix tiers, same reasoning ComparisonView
  // uses to leave its own Smart Split card unbadged.
  tier?: 'verified' | 'estimate';
}

interface BasketOptimizationCardProps {
  basketItemCount: number;
  bestSingleStore: StoreRank | null;
  smartSplit: {
    total: number;
    route: SplitItem[];
    // Real Mapbox driving distance/time for this route, when coordinates were
    // available — text-only here, no map (the full route map lives on Basket
    // IQ's own page, components/ComparisonView.tsx).
    routeStats?: {
      totalDistanceMiles?: number;
      totalDurationMinutes?: number;
      extraMinutesVsSingleStore?: number;
      // Distance + time cost of the route in dollars (IRS mileage rate + the
      // user's value-of-time setting) — Basket IQ's route cost model.
      tripCostDollars?: number;
    } | null;
  } | null;
  otherOptions?: StoreRank[];
  activeCurrencySymbol?: string;
}

export const BasketOptimizationCard: React.FC<{ props: BasketOptimizationCardProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t } = useTranslation();
  const { userProfile, currency } = useStore();
  const { 
    basketItemCount, 
    bestSingleStore, 
    smartSplit, 
    otherOptions = [],
    activeCurrencySymbol: propCurrencySymbol
  } = props || {};

  const activeCurrencySymbol = React.useMemo(() => {
    return CURRENCIES.find(c => c.code === (currency || userProfile?.settings?.interface?.currency))?.symbol || propCurrencySymbol || '$';
  }, [userProfile?.settings?.interface?.currency, propCurrencySymbol]);

  const uniqueStoresInSplit = React.useMemo(() => {
    if (!smartSplit?.route) return [];
    return Array.from(new Set(smartSplit.route.map(p => p.store)));
  }, [smartSplit?.route]);

  if (!bestSingleStore && !smartSplit) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl text-center animate-pulse">
           <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{t('comparison.optimizer_engine')}...</p>
        </div>
      );
    }
    return null;
  }

  const itemsMapped = smartSplit?.route?.length || 0;
  const showSmartSplit = !!(smartSplit?.route && (uniqueStoresInSplit.length > 1 || (bestSingleStore && smartSplit.total < bestSingleStore.total)));

  // Nothing to compare (e.g. only the current store has prices): every section
  // below would be hidden and the card rendered as an empty box.
  if (!showSmartSplit && !bestSingleStore && otherOptions.length === 0) {
    return (
      <div className="bg-white rounded-[2rem] p-5 border border-gray-100 shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 shrink-0">
            <BrainCircuit size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black text-[#1a2b3c] leading-tight">{t('comparison.no_alternatives_title')}</p>
            <p className="text-[12px] font-medium text-gray-500 leading-relaxed mt-1">{t('comparison.no_alternatives_desc')}</p>
          </div>
        </div>
        {smartSplit && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('comparison.items_mapped', { count: itemsMapped })}</p>
              <p className="text-[13px] font-black text-[#1a2b3c] truncate flex items-center gap-1.5 mt-0.5">
                <MapPin size={12} className="text-emerald-500 shrink-0" />
                {uniqueStoresInSplit[0] || '—'}
              </p>
            </div>
            <p className="text-xl font-black text-[#1a2b3c] tracking-tight tabular-nums shrink-0">
              {activeCurrencySymbol}{(smartSplit.total ?? 0).toFixed(2)}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Smart Split Strategy Card - Show if multiple stores OR if it saves money */}
      {showSmartSplit && smartSplit && (
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-[2.5rem] p-6 text-white shadow-xl shadow-indigo-200 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <Crown size={60} />
          </div>
          <div className="relative z-10 space-y-4">
            <div className="flex items-center justify-between">
              <div className="px-3 py-1 bg-white/20 rounded-full border border-white/10">
                <span className="text-[8px] font-black uppercase tracking-widest">{t('comparison.opt_strategy')}</span>
              </div>
              <div className="flex items-center gap-1.5 text-indigo-200">
                <Layers size={12} />
                <span className="text-[9px] font-bold uppercase">{t('comparison.items_mapped', { count: itemsMapped })}</span>
              </div>
            </div>
            
            <div>
              <p className="text-[9px] font-black uppercase text-indigo-200 tracking-widest mb-1">{t('comparison.total_smart_split')}</p>
              <h4 className="text-3xl font-black tracking-tighter">
                {activeCurrencySymbol}{(smartSplit.total ?? 0).toFixed(2)}
              </h4>
              {/* Only a store covering every item is a valid "go to one store
                  instead" baseline — bestSingleStore may only be the highest-
                  coverage store, not a full-basket one, so comparing its total
                  to smartSplit.total otherwise produces a nonsensical figure. */}
              {smartSplit.routeStats?.tripCostDollars == null && bestSingleStore && bestSingleStore.matches === basketItemCount && (smartSplit.total ?? 0) < (bestSingleStore.total ?? 0) && (
                <p className="text-[9px] font-bold text-emerald-300 mt-1 uppercase tracking-wide">
                  {t('chat.save_vs_single', { amount: `${activeCurrencySymbol}${((bestSingleStore.total ?? 0) - (smartSplit.total ?? 0)).toFixed(2)}` })}
                </p>
              )}
              {smartSplit.routeStats?.tripCostDollars == null && bestSingleStore && bestSingleStore.matches === basketItemCount && (smartSplit.total ?? 0) >= (bestSingleStore.total ?? 0) && uniqueStoresInSplit.length > 1 && (
                <p className="text-[9px] font-bold text-indigo-200/60 mt-1 uppercase tracking-wide italic">
                  {t('comparison.multi_store_no_savings', { defaultValue: 'Multi-store route (Same or higher cost)' })}
                </p>
              )}
              {/* Trip cost (distance + time, per Basket IQ's route cost model)
                  added on top of item price — replaces the vs-single-store
                  savings framing above once a real figure is available. */}
              {smartSplit.routeStats?.tripCostDollars != null && (
                <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
                  <div className="flex items-center justify-between text-[9px] font-bold text-indigo-200 uppercase tracking-wide">
                    <span>{t('comparison.route_trip_cost')}</span>
                    <span>+{activeCurrencySymbol}{smartSplit.routeStats.tripCostDollars.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] font-black text-white uppercase tracking-wide">
                    <span>{t('comparison.route_true_total')}</span>
                    <span>{activeCurrencySymbol}{((smartSplit.total ?? 0) + smartSplit.routeStats.tripCostDollars).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2 pt-2 border-t border-white/10">
              <p className="text-[8px] font-black uppercase text-indigo-300 tracking-widest">{t('comparison.route_recommendation')}</p>
              <div className="grid grid-cols-1 gap-1.5">
                {uniqueStoresInSplit.map((store, i) => (
                  <div key={`${store}-${i}`} className="bg-white/10 border border-white/5 p-2 rounded-xl flex items-center justify-between">
                    <span className="text-[9px] font-black truncate pr-4">{store}</span>
                    <span className="text-[9px] font-black text-indigo-200 shrink-0">
                      {smartSplit.route.filter(p => p.store === store).length} {t('history.items_count', { count: smartSplit.route.filter(p => p.store === store).length }).split(' ')[1]}
                    </span>
                  </div>
                ))}
              </div>
              {smartSplit.routeStats?.totalDistanceMiles != null && smartSplit.routeStats?.totalDurationMinutes != null && (
                <p className="text-[8px] font-bold text-indigo-200/80 uppercase tracking-wide">
                  {t('comparison.route_total_stats', {
                    miles: smartSplit.routeStats.totalDistanceMiles.toFixed(1),
                    minutes: Math.round(smartSplit.routeStats.totalDurationMinutes),
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Best Single Store Card */}
      {bestSingleStore && (
        <div className="bg-white rounded-[2rem] p-5 border border-emerald-500 shadow-lg shadow-emerald-50 ring-1 ring-emerald-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
            <Zap size={60} />
          </div>
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="px-2 py-0.5 bg-emerald-500 text-white rounded-full text-[7px] font-black uppercase tracking-tighter shadow-sm shadow-emerald-500/20">{t('comparison.best_single_store')}</div>
                <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">{t('comparison.items_of_total', { count: bestSingleStore.matches, total: basketItemCount })}</span>
              </div>
              <h5 className="font-black text-[#1a2b3c] text-sm truncate uppercase tracking-tight">{bestSingleStore.storeName}</h5>
            </div>
            <div className="text-right pl-4">
              <p className="text-xl font-black text-[#1a2b3c] tracking-tighter">{activeCurrencySymbol}{(bestSingleStore.total ?? 0).toFixed(2)}</p>
              <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{t('common.total')}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-3 border-t border-gray-50 relative z-10">
             <div className="w-full bg-gray-50 h-1.5 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, Number(bestSingleStore.coverage) || 0))}%`, backgroundImage: CHART_GRADIENT_CSS }} />
             </div>
             <span className="text-[8px] font-black text-gray-400 uppercase ml-3 shrink-0">{(bestSingleStore.coverage ?? 0).toFixed(0)}% {t('comparison.coverage')}</span>
          </div>
        </div>
      )}

      {/* Comparison Leaderboard */}
      {otherOptions.length > 0 && (
        <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
             <BarChart3 size={12} className="text-gray-400" />
             <h5 className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t('comparison.single_store_ranking')}</h5>
          </div>
          <div className="p-4 space-y-2">
            {otherOptions.map((opt, i) => (
              <div key={`${opt.storeName}-${i}`} className="flex items-center justify-between p-3 rounded-2xl hover:bg-gray-50 transition-colors group">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-black text-[#1a2b3c] truncate uppercase tracking-tight">{opt.storeName}</p>
                  <p className="text-[8px] font-bold text-gray-400 uppercase tracking-wide">{t('history.items_count', { count: opt.matches })}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-black text-[#1a2b3c]">{activeCurrencySymbol}{(opt.total ?? 0).toFixed(2)}</p>
                  {bestSingleStore && (
                    <p className="text-[8px] font-bold text-rose-500">+{activeCurrencySymbol}{((opt.total ?? 0) - (bestSingleStore.total ?? 0)).toFixed(2)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
